/**
 * The reader's type-scale settings: the five sizes and three line heights, and
 * the CSS custom properties that apply them.
 *
 * Separate from `ReaderControls.tsx` because fast refresh only works when a
 * file exports components alone — mixing constants in means an edit to the
 * control remounts the whole subtree and loses the reader's scroll position.
 */

export const READER_STEPS = [1, 2, 3, 4, 5] as const;
export type ReaderStep = (typeof READER_STEPS)[number];

export const LEADINGS = ['tight', 'normal', 'loose'] as const;
export type Leading = (typeof LEADINGS)[number];

/**
 * Guards for the stored values.
 *
 * Here rather than in the reader component because the settings screen reads
 * the same keys, and importing a guard from a file that also exports a
 * component is what breaks Fast Refresh.
 */
export const isStep = (v: unknown): v is `${ReaderStep}` =>
  typeof v === 'string' && ['1', '2', '3', '4', '5'].includes(v);

export const isLeading = (v: unknown): v is Leading =>
  typeof v === 'string' && (LEADINGS as readonly string[]).includes(v);

export const LEADING_LABELS: Record<Leading, string> = {
  tight: 'Tight',
  normal: 'Normal',
  loose: 'Loose',
};

/**
 * How much of the reader's pane the text is allowed to use.
 *
 * Three widths, narrowest first, because that is the order they appear in the
 * control:
 *
 * - `measure` caps at `--reader-measure` (40rem) and centres. The brand book's
 *   own cap, and the easiest line length to read.
 * - `normal` caps at `--container-7xl` (80rem) and centres. **The default.**
 *   It is deliberately the same width as the Home and Library screens resolve
 *   `max-w-7xl` to, so moving between a list and a khutbah does not move the
 *   page under the reader. The token is Tailwind's own, not a literal, so the
 *   two cannot drift apart.
 * - `full` lets the khutbah fill the pane it was given, the way quran.com
 *   does. No cap at all.
 *
 * A DELIBERATE DEPARTURE, recorded rather than quiet. The brand book says
 * "Translation columns cap at `reader-measure` (40rem)" and these two wider
 * settings make that cap one of three rather than mandatory. It is the
 * product owner's call, asked for three times now; the typographic argument
 * for 40rem has not gone away -- long lines are harder to track back to the
 * next line -- which is why `measure` is still here and still one click away.
 *
 * `full` was the default until the product owner asked for a middle setting
 * and for it to be the one a reader starts in. A stored `'full'` stays valid,
 * so nobody who already chose it is moved.
 */
export const READING_WIDTHS = ['measure', 'normal', 'full'] as const;
export type ReadingWidth = (typeof READING_WIDTHS)[number];

export const READING_WIDTH_LABELS: Record<ReadingWidth, string> = {
  measure: 'Narrow column',
  normal: 'Normal width',
  full: 'Full width',
};

/**
 * The cap each width applies, as a CSS value.
 *
 * `normal` uses Tailwind's own container token -- the same value `max-w-7xl`
 * resolves to on Home and Library -- rather than an 80rem literal, so the
 * reader cannot drift away from the screens it sits between. `full` has no
 * cap, which is what `null` says.
 */
export const READING_WIDTH_CAPS: Record<ReadingWidth, string | null> = {
  measure: 'var(--reader-measure)',
  normal: 'var(--container-7xl)',
  full: null,
};

export function isReadingWidth(value: unknown): value is ReadingWidth {
  return (
    typeof value === 'string' && (READING_WIDTHS as readonly string[]).includes(value)
  );
}

/**
 * The custom properties the reader root carries.
 *
 * Both reference design-system tokens rather than literal values, so the five
 * sizes and three leadings stay the design system's and not ours. Returned as
 * a style object so React owns it and it cannot drift from the state that
 * produced it.
 *
 * Changing `--reader-step` scales EVERY script, never Arabic alone — that
 * relationship between the Arabic and its translation is the whole point of
 * the control. `--reader-leading` is the base, and `globals.css` adds 0.45 for
 * Urdu and 0.2 for Arabic on top; Nastaliq and tashkeel collide without it.
 */
export function readerStyle(step: ReaderStep, leading: Leading): React.CSSProperties {
  return {
    '--reader-step': `var(--reader-step-${step})`,
    '--reader-leading': `var(--reader-leading-${leading})`,
  } as React.CSSProperties;
}
